/**
 * LIFF 進入點
 *
 * 1. liff.init() → 取 accessToken + profile
 * 2. POST /api/auth/liff-verify → 拿 yannwo JWT
 * 3. 根據會員狀態跳轉：
 *    - status=suspended → 停在這裡顯示「會員資格暫停」（別放進去逛完才在寫入時被擋）
 *    - status 不是 active（pending / 缺欄位 / 不認得的值）→ /join 一鍵加入（帶著 ?next=）
 *    - status=active → ?next= 或 /home
 *
 * 2026-09-24 拿掉入會表單 + 驗證碼:這裡不再看 phoneVerified。
 * 姓名 / 手機改成結帳 / 送禮前才補(Checkout / GiftWizard 自己導 /complete-profile)。
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { initLiff } from "../liff";
import { liffVerify } from "../api/members";
import { useAuthStore } from "../store/authStore";
import { useMemberStore } from "../store/memberStore";
import { Leaf } from "lucide-react";
import LoadingScreen from "../components/LoadingScreen";
import StatusMark from "../components/StatusMark";
import { safeNext, withNext } from "../utils/join";

export default function Splash() {
  const navigate = useNavigate();
  const location = useLocation();
  // attempt 變化會 trigger useEffect 重跑(retry 按鈕按下時 +1)
  const [attempt, setAttempt] = useState(0);
  const [errored, setErrored] = useState(false);
  // 會員資格被後台停用 — 放行進 /home 的話,人會一路填完表單才在送出時被後端擋
  // (2026-09-16 送禮 sender_not_active 事故),所以擋在入口並講人話。
  const [suspended, setSuspended] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErrored(false);
      try {
        const liffProfile = await initLiff();
        if (cancelled) return;
        if (!liffProfile) {
          // dev mode 或正在跳轉 login,等下次回來(會被 redirect 接住)
          return;
        }
        const { jwt, member } = await liffVerify({
          accessToken: liffProfile.accessToken,
          profile: {
            userId: liffProfile.userId,
            displayName: liffProfile.displayName,
            pictureUrl: liffProfile.pictureUrl,
          },
        });
        if (cancelled) return;
        useAuthStore.getState().setMemberJwt(jwt, member.userId);
        useMemberStore.getState().setMember({
          line_user_id: member.userId,
          name: member.name,
          display_name: member.displayName,
          // phone 一定要帶:Checkout / GiftWizard 用 store 裡的 member 判「要不要先補姓名手機」,
          // 少了這欄舊會員也會被當成沒手機、被彈去補資料頁
          phone: member.phone,
          tier: member.tier,
          status: member.status,
          phone_verified: member.phoneVerified,
        });

        // 路由決策:active 才尊重 ?next=,避免 user 還沒加入就被丟到 /cart 看不懂為什麼空白。
        // 非 active 帶著 next 去 /join,一鍵加入完直接到原本要去的頁。
        const next = safeNext(new URLSearchParams(location.search).get("next"));
        if (member.status === "suspended") {
          setSuspended(true);
        } else if (member.status !== "active") {
          // pending,或「狀態不明」—— 殘缺 member doc(沒有 status 欄位)、
          // 後端未來新增的狀態值、null,一律當成沒加入,走一鍵加入。
          // join_member 會順手把半截 doc 的 base 欄位補完整。
          navigate(next ? withNext("/join", next) : "/join", { replace: true });
        } else if (next) {
          navigate(next, { replace: true });
        } else {
          navigate("/home", { replace: true });
        }
      } catch (e: any) {
        if (cancelled) return;
        console.error("[Splash] init failed", e);
        setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, attempt, location.search]);

  if (suspended) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6">
        <StatusMark icon={Leaf} />
        <div className="mt-3 text-ink font-medium">您的會員資格目前暫停中</div>
        <div className="mt-2 text-sm text-muted text-center leading-relaxed">
          暫時無法下單或使用會員服務。
          <br />
          請回到 LINE 輸入「諮詢」與專屬顧問聯繫,我們會盡快為您處理。
        </div>
      </div>
    );
  }

  if (errored) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream px-6">
        <StatusMark icon={Leaf} />
        <div className="mt-3 text-ink font-medium">無法連線霽雲</div>
        <div className="mt-2 text-sm text-muted text-center">
          請確認網路狀態,或稍候再試一次
        </div>
        <button
          onClick={() => setAttempt((n) => n + 1)}
          className="btn-primary mt-6 px-8"
        >
          重新連線
        </button>
      </div>
    );
  }

  return <LoadingScreen label="正在連線霽雲…" />;
}
